import {useEffect, useState} from 'react';
import {ACCOUNT_ID} from '../../api/account';
import {cancelSubscription, removeAttachedCard} from '../../api/payment';
import {showError} from '../../utils/notify';

export default function MyContentPage({
                                          skazkiTales,
                                          skazkiCredits,
                                          subscription,
                                          plans,
                                          linkedTelegramId,
                                          linkedMaxId,
                                          fetchAccountStatus,
                                          fetchTales,
                                          onBuyCredits,
                                          onSkazkiOpen,
                                          onSkazkiClick,
                                      }) {
    const [cancelling, setCancelling] = useState(false);
    const [removingCard, setRemovingCard] = useState(false);
    const handleCancelSubscription = async () => {
        if (!confirm('Отменить подписку? Доступ сохранится до конца оплаченного периода.')) return;
        setCancelling(true);
        try {
            await cancelSubscription({accountId: ACCOUNT_ID});
            await fetchAccountStatus?.();
        } catch (e) {
            console.error(e);
            showError('Не удалось отменить подписку. Попробуйте ещё раз.');
        } finally {
            setCancelling(false);
        }
    };
    const handleRemoveCard = async () => {
        if (!confirm('Удалить привязанную карту? Автопродление отключится, доступ сохранится до конца оплаченного периода.')) return;
        setRemovingCard(true);
        try {
            await removeAttachedCard({accountId: ACCOUNT_ID});
            await fetchAccountStatus?.();
        } catch (e) {
            console.error(e);
            showError('Не удалось удалить карту. Попробуйте ещё раз.');
        } finally {
            setRemovingCard(false);
        }
    };
    useEffect(() => {
        const refresh = () => {
            fetchAccountStatus?.();
            fetchTales?.();
        };
        refresh();
        const interval = setInterval(refresh, 30000);
        return () => clearInterval(interval);
    }, []);

    const items = [...(skazkiTales || [])]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const subscriptionPlan = subscription?.plan_id
        ? (plans?.subscriptions || []).find(p => p.id === subscription.plan_id)
        : null;
    const subscriptionPlanLabel = subscriptionPlan?.name || subscription?.plan_id;

    const periodEndLabel = subscription?.current_period_end
        ? new Date(subscription.current_period_end).toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        })
        : null;

    return (
        <div className="max-w-[1110px] mx-auto px-4 py-10">
            {/* Subscription Panel */}
            {subscription && (
                <div
                    className="bg-white border border-brand-dark/10 rounded-[20px] p-5 mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                        <p className="text-sm font-medium text-brand-dark/60">Ваша подписка</p>
                        <p className="text-lg font-bold text-brand-dark">{subscriptionPlanLabel}</p>
                        {periodEndLabel && (
                            <p className="text-sm text-brand-dark/60">
                                {subscription.cancel_at_period_end
                                    ? `Доступ до ${periodEndLabel}, автопродление отключено`
                                    : `Следующее списание: ${periodEndLabel}`}
                            </p>
                        )}
                    </div>
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:flex-wrap">
                        {!subscription.cancel_at_period_end && subscription.status === 'active' && (
                            <button
                                onClick={handleCancelSubscription}
                                disabled={cancelling}
                                className="px-5 py-2.5 bg-brand-gray text-brand-dark text-sm font-semibold rounded-xl hover:bg-gray-200 transition disabled:opacity-50"
                            >
                                {cancelling ? 'Отменяем...' : 'Отменить подписку'}
                            </button>
                        )}
                        {subscription.has_payment_method && (
                            <button
                                onClick={handleRemoveCard}
                                disabled={removingCard}
                                className="px-5 py-2.5 bg-brand-gray text-brand-dark text-sm font-semibold rounded-xl hover:bg-gray-200 transition disabled:opacity-50"
                            >
                                {removingCard ? 'Удаляем...' : 'Удалить карту'}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Credits Banner */}
            <div
                className="bg-gradient-to-r from-brand-red/10 via-brand-pink/10 to-brand-orange/10 rounded-[20px] p-5 mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <p className="text-lg font-medium text-brand-dark">
                        Баланс: <span className="text-2xl font-bold text-brand-red">{skazkiCredits}</span> сказок
                    </p>
                    <p className="text-sm text-brand-dark/60">
                        {skazkiCredits === 0
                            ? subscription
                                ? 'Новые сказки будут доступны при продлении подписки'
                                : 'Оформите подписку, чтобы создавать сказки'
                            : 'Доступно для создания'
                        }
                    </p>
                </div>
                <button
                    onClick={onBuyCredits}
                    className="px-6 py-3 bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-semibold rounded-xl hover:opacity-90 transition"
                >
                    {subscription ? 'Создать сказку' : 'Оформить подписку'}
                </button>
            </div>

            {/* Header */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-8">
                <h1 className="text-[28px] md:text-[42px] font-bold text-brand-dark leading-[1.1] tracking-[-0.42px]">
                    Мои сказки
                </h1>
                <div className="flex gap-2">
                    <button
                        onClick={onSkazkiClick}
                        className="px-6 py-3 bg-brand-gray text-brand-dark text-sm font-semibold rounded-xl hover:bg-gray-200 transition"
                    >
                        Создать сказку
                    </button>
                </div>
            </div>

            {/* List */}
            {items.length === 0 ? (
                <div className="bg-brand-gray rounded-[20px] md:rounded-[30px] p-10 text-center">
                    <h3 className="text-2xl font-bold text-brand-dark mb-2">Тут могли бы быть ваши сказки</h3>
                    <p className="text-lg font-medium text-brand-dark/60">
                        но вы ещё ничего не создали
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {items.map(item => (
                        <div key={`s-${item.id}`}
                             className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4 p-5 bg-brand-gray rounded-[20px] hover:bg-gray-100 transition">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span
                                        className="text-xs font-semibold px-2 py-0.5 rounded-md bg-brand-dark/10 text-brand-dark/60">Сказка</span>
                                    {item.paid === false && (
                                        <span
                                            className="text-xs font-semibold px-2 py-0.5 rounded-md bg-amber-100 text-amber-700">превью</span>
                                    )}
                                    <span className="font-bold text-lg text-brand-dark">{item.title}</span>
                                </div>
                                <div className="text-sm text-brand-dark/60 truncate">
                                    для {item.child_name}, {item.age} лет
                                </div>
                                <div className="text-xs text-brand-dark/40 mt-1">
                                    {new Date(item.created_at).toLocaleString('ru-RU')}
                                </div>
                            </div>
                            <div className="flex-shrink-0">
                                <span
                                    className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${item.status === 'ready' ? 'bg-green-100 text-green-700' : item.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {item.status === 'ready' ? 'Готово' : item.status === 'failed' ? 'Ошибка' : 'Генерация...'}
                                </span>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                                {item.status === 'ready' && (
                                    <button
                                        onClick={() => onSkazkiOpen(item.id)}
                                        className="flex-1 md:flex-none px-4 md:px-5 py-3 bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-semibold rounded-xl hover:opacity-90 transition"
                                    >
                                        Слушать
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
